import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { apiGet } from '../utils/api';
import toast from 'react-hot-toast';
import './Home.css'; // Make sure to import the CSS

function Home() {
  const [albums, setAlbums] = useState([]);
  // const [testimonials, setTestimonials] = useState([]); // Removed unused variable
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  // High-quality wedding images from Unsplash
  const carouselImages = [
    {
      url: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      alt: 'Bride and groom sharing intimate moment',
      caption: 'Intimate Wedding Moments'
    },
    {
      url: 'https://i.postimg.cc/5N8wGzBp/YZ_34.jpg',
      alt: 'Wedding ceremony with flower petals',
      caption: 'Ceremony Elegance'
    },
    {
      url: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      alt: 'Bride with beautiful bouquet',
      caption: 'Bridal Beauty'
    },
    {
      url: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      alt: 'Wedding reception decorations',
      caption: 'Magical Receptions'
    },
    {
      url: 'https://images.unsplash.com/photo-1507504031003-b417219a0fde?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      alt: 'Couple dancing at wedding',
      caption: 'Celebration & Joy'
    },
    {
      url: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      alt: 'Wedding rings on flowers',
      caption: 'Timeless Details'
    },
    {
      url: 'https://i.postimg.cc/5ynF496j/YZz_43.jpg',
      alt: 'Wedding couple outdoors',
      caption: 'Natural Portraits'
    }
  ];

  // Featured album covers (using actual album data or fallback images)
  const albumCovers = [
    {
      url: 'https://i.postimg.cc/PfZ3tgXm/YZz_4.jpg',
      title: 'Elegant Wedding'
    },
    {
      url: 'https://i.postimg.cc/sxdM9nNf/YZ_63.jpg',
      title: 'Romantic Celebration'
    },
    {
      url: 'https://i.postimg.cc/d3rTbMy9/YZ_41.jpg',
      title: 'Garden Wedding'
    },
    {
      url: 'https://i.postimg.cc/XYm4CrPR/YZ_83.jpg',
      title: 'Traditional Ceremony'
    }
  ];

  // Three featured images with captions at different positions
  const featuredImages = [
    {
      url: 'https://i.postimg.cc/5N8wGzBp/YZ_34.jpg',
      alt: 'Bride and groom intimate moment',
      caption: 'Intimate Moments',
      position: 'left'
    },
    {
      url: 'https://i.postimg.cc/GmBsRCRZ/YZz_64.jpg',
      alt: 'Bride with bouquet',
      caption: 'Bridal Elegance',
      position: 'center'
    },
    {
      url: 'https://i.postimg.cc/Ssc6hp6q/YZz_18.jpg',
      alt: 'Wedding reception',
      caption: 'Celebration & Joy',
      position: 'right'
    }
  ];

  // Carousel settings
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    fade: true,
    cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)',
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: dots => (
      <div style={{ position: 'absolute', bottom: '30px', width: '100%', zIndex: 10 }}>
        <ul style={{ margin: '0', display: 'flex', justifyContent: 'center', gap: '10px' }}> {dots} </ul>
      </div>
    ),
    customPaging: i => (
      <div
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: i === currentSlide ? '#fff' : 'rgba(255,255,255,0.5)',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
      />
    )
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Only fetch albums data since testimonials is not used
      const albumsData = await apiGet('albums');
      setAlbums(Array.isArray(albumsData) ? albumsData : []);
      
      // Optional: Still fetch testimonials if you plan to use them later
      // const testimonialsData = await apiGet('testimonials');
      // setTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Carousel Section */}
      <section className="hero-carousel">
        <Slider ref={sliderRef} {...carouselSettings}>
          {carouselImages.map((image, index) => (
            <div key={index} className="carousel-slide">
              <div 
                className="carousel-image"
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${image.url})`,
                }}
              />
              <div className="carousel-caption">
                <h2>{image.caption}</h2>
                <p>Capturing your love story with elegance and artistry</p>
                <Link to="/portfolio" className="button carousel-button">
                  View Portfolio
                </Link>
              </div>
            </div>
          ))}
        </Slider>

        {/* Custom navigation arrows */}
        <button 
          onClick={() => sliderRef.current?.slickPrev()}
          className="carousel-arrow prev"
        >
          ←
        </button>
        <button 
          onClick={() => sliderRef.current?.slickNext()}
          className="carousel-arrow next"
        >
          →
        </button>

        {/* Slide counter */}
        <div className="slide-counter">
          {currentSlide + 1} / {carouselImages.length}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="section">
        <div className="container">
          <div className="welcome-content">
            <h1>Telling Your Story Through Timeless Wedding Imagery</h1>
            <p>
              CineHouseStudio specializes in cinematic wedding photography and videography 
              rooted in authenticity, elegance, and tradition. Based in Toronto, we capture 
              love stories across Canada with heart and artistic depth. Every frame tells a 
              story, every moment becomes a memory.
            </p>
          </div>
        </div>
      </section>

      {/* Three Featured Images Section with Varied Caption Positions */}
      <section className="section accent-bg">
        <div className="container">
          <h2 className="section-title">Featured Moments</h2>
          <div className="featured-grid">
            {featuredImages.map((image, index) => (
              <div 
                key={index} 
                className={`featured-card featured-${image.position}`}
              >
                <div className="featured-image-wrapper">
                  <img 
                    src={image.url} 
                    alt={image.alt}
                    className="featured-image"
                  />
                  <div className={`featured-caption caption-${image.position}`}>
                    <h3>{image.caption}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider thick" />

      {/* About Section with Image Gallery */}
      <section className="section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>More Than Just a Name</h2>
              <blockquote>
                <p>CineHouseStudio is more than just a name — it's a reflection of our essence.</p>
              </blockquote>
              <p>
                We immerse ourselves in your celebration, your energy, your love, 
                and let it guide our lens. Every wedding is unique, and we approach 
                each story with fresh eyes and an open heart, ensuring your memories 
                are captured authentically and beautifully.
              </p>
            </div>
            <div className="about-image">
              <div className="image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1507504031003-b417219a0fde?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                  alt="Wedding preparation"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Albums */}
      {albums.length > 0 && (
        <section className="section accent-bg">
          <div className="container">
            <h2 className="section-title">Featured Albums</h2>
            <div className="albums-grid">
              {albums.slice(0, 3).map((album, index) => (
                <Link to={`/gallery/${album.albumId}`} key={album.albumId} className="album-card">
                  <div className="album-image">
                    <img 
                      src={albumCovers[index % albumCovers.length].url}
                      alt={album.coupleName}
                    />
                  </div>
                  <div className="album-info">
                    <h3>{album.coupleName}</h3>
                    <p className="album-date">
                      {new Date(album.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <span className="view-gallery">
                      View Gallery →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link to="/portfolio" className="button">
                View All Albums
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-grid">
            <div className="cta-image">
              <img 
                src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                alt="Happy couple"
              />
            </div>
            <div className="cta-content">
              <h2>Let's Capture Your Story</h2>
              <p>
                Ready to create timeless memories? Get in touch with us to discuss 
                your vision and how we can bring it to life. Your love story deserves 
                to be told beautifully.
              </p>
              <Link to="/contact" className="button">
                Contact Us Today
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;